import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { uploadMoreImages } from './features/slices';
import { NotificationContainer } from "react-notifications";
import "react-notifications/lib/notifications.css";
import './UploadMoreImages.css';

const UploadMoreImages = () => {
    const dispatch = useDispatch();
    const [imageData, setImageData] = useState([]);
    const [uploadResults, setUploadResults] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [progress, setProgress] = useState(0);

    // Set cứng mảng server URLs trong code
    const serverUrls = [
        'https://coral-app-uat4g.ondigitalocean.app/upload',
        'https://coral-app-uat4g.ondigitalocean.app/upload',
        'https://hammerhead-app-2-cgqkw.ondigitalocean.app/upload',
        'https://hammerhead-app-2-cgqkw.ondigitalocean.app/upload',
        'https://lobster-app-ywmav.ondigitalocean.app/upload',
        'https://lobster-app-ywmav.ondigitalocean.app/upload',
        'https://goldfish-app-2xdcx.ondigitalocean.app/upload',
        'https://goldfish-app-2xdcx.ondigitalocean.app/upload',
        'https://sea-lion-app-deyzx.ondigitalocean.app/upload',
        'https://sea-lion-app-deyzx.ondigitalocean.app/upload',
    ];

    const onDrop = (acceptedFiles) => {
        const newImages = acceptedFiles.map((file, index) => {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const base64String = btoa(event.target.result);
                    resolve({
                        index: imageData.length + index,
                        base64: base64String,
                        fileName: file.name,
                        file: file
                    });
                };
                reader.readAsBinaryString(file);
            });
        });

        Promise.all(newImages).then((results) => {
            setImageData(prev => [...prev, ...results]);
        });
    };

    const { getRootProps, getInputProps } = useDropzone({ onDrop });

    const handleUpload = async () => {
        if (imageData.length === 0) return;

        setIsUploading(true);
        setProgress(0);
        setUploadResults([]);

        // Chia ảnh thành các batch, mỗi batch tối đa 10 ảnh
        const MAX_IMAGES_PER_BATCH = 10;
        const batches = [];

        for (let i = 0; i < imageData.length; i += MAX_IMAGES_PER_BATCH) {
            batches.push(imageData.slice(i, i + MAX_IMAGES_PER_BATCH));
        }

        const allResults = [];
        let completedCount = 0;
        const serverCount = serverUrls.length;

        // Chia batches thành các nhóm, mỗi nhóm = số lượng server
        for (let groupStart = 0; groupStart < batches.length; groupStart += serverCount) {
            const batchGroup = batches.slice(groupStart, groupStart + serverCount);

            const groupPromises = batchGroup.map(async (batch, groupIndex) => {
                const batchIndex = groupStart + groupIndex;
                try {
                    const imagesArray = batch.map(image => ({
                        base64: image.base64,
                        index: image.index,
                        fileName: image.fileName,
                        type: ""
                    }));

                    const batchResults = await dispatch(uploadMoreImages({
                        images: imagesArray,
                        serverIndex: groupIndex,
                        serverUrls: serverUrls
                    })).unwrap();

                    completedCount += batch.length;
                    const progressPercent = (completedCount / imageData.length) * 100;
                    setProgress(progressPercent);

                    allResults.push(...batchResults);
                    return batchResults;
                } catch (error) {
                    console.error(`Batch ${batchIndex} failed:`, error);
                    const errorResults = batch.map(image => ({
                        index: image.index,
                        url: '',
                        status: 'error',
                        error: error.message || 'Batch upload failed',
                        fileName: image.fileName
                    }));
                    completedCount += batch.length;
                    const progressPercent = (completedCount / imageData.length) * 100;
                    setProgress(progressPercent);
                    allResults.push(...errorResults);
                    return errorResults;
                }
            });

            await Promise.all(groupPromises);
        }

        allResults.sort((a, b) => a.index - b.index);
        setUploadResults(allResults);
        setIsUploading(false);
    };

    const handleExportSheet = () => {
        if (uploadResults.length === 0) return;

        const sheetData = [
            ['Index', 'File Name', 'URL', 'Status']
        ];

        uploadResults.forEach(result => {
            sheetData.push([
                result.index,
                result.fileName || '',
                result.url || result.error || '',
                result.status === 'success' ? 'Success' : 'Error'
            ]);
        });

        const ws = XLSX.utils.aoa_to_sheet(sheetData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Upload Results');
        XLSX.writeFile(wb, `upload_results_${moment().unix()}.xlsx`);
    };

    const successCount = uploadResults.filter(r => r.status === 'success').length;
    const errorCount = uploadResults.filter(r => r.status === 'error').length;

    return (
        <div className="upload-more-images-container">
            <NotificationContainer />

            {/* Upload Section */}
            <div className="upload-section">
                <h2 className="section-title">Upload More Images</h2>
                <div {...getRootProps()} className="dropzone">
                    <input {...getInputProps()} />
                    <div className="dropzone-content">
                        <div className="dropzone-icon">📁</div>
                        <p className="dropzone-text">Kéo và thả hình ảnh vào đây</p>
                        <p className="dropzone-subtext">hoặc nhấn để chọn file (có thể chọn nhiều file)</p>
                    </div>
                </div>
            </div>

            {/* Selected Images List */}
            {imageData.length > 0 && (
                <div className="upload-section">
                    <div className="section-header">
                        <h2 className="section-title">Hình ảnh đã chọn ({imageData.length})</h2>
                    </div>
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Index</th>
                                    <th>File Name</th>
                                </tr>
                            </thead>
                            <tbody>
                                {imageData.map((data, index) => (
                                    <tr key={index}>
                                        <td>{data.index}</td>
                                        <td>{data.fileName || `Image ${data.index + 1}`}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Upload Progress */}
            {isUploading && (
                <div className="upload-section">
                    <h2 className="section-title">Đang upload...</h2>
                    <div className="progress-container">
                        <div className="progress-bar">
                            <div
                                className="progress-fill"
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                        <p className="progress-text">
                            {progress.toFixed(1)}% ({Math.round(progress / 100 * imageData.length)}/{imageData.length})
                        </p>
                    </div>
                </div>
            )}

            {/* Upload Results */}
            {uploadResults.length > 0 && (
                <>
                    <div className="upload-section">
                        <div className="section-header">
                            <h2 className="section-title">Kết quả upload</h2>
                            <div className="stats">
                                <span className="stat-success">Thành công: {successCount}</span>
                                <span className="stat-error">Thất bại: {errorCount}</span>
                            </div>
                        </div>
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Index</th>
                                        <th>File Name</th>
                                        <th>URL</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {uploadResults.map((result, index) => (
                                        <tr key={index}>
                                            <td>{result.index}</td>
                                            <td>{result.fileName || '-'}</td>
                                            <td className="url-cell">
                                                {result.url ? (
                                                    <a
                                                        href={result.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="url-link"
                                                    >
                                                        {result.url}
                                                    </a>
                                                ) : (
                                                    <span className="error-text">{result.error || 'N/A'}</span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`status-badge ${result.status === 'success' ? 'success' : 'error'}`}>
                                                    {result.status === 'success' ? '✓ Success' : '✗ Error'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="upload-section">
                        <button
                            className="btn btn-primary btn-export"
                            onClick={handleExportSheet}
                        >
                            Xuất Excel Sheet
                        </button>
                    </div>
                </>
            )}

            {/* Upload Button */}
            {!isUploading && imageData.length > 0 && uploadResults.length === 0 && (
                <div className="upload-section">
                    <button
                        className="btn btn-primary btn-upload"
                        onClick={handleUpload}
                    >
                        Bắt đầu Upload
                    </button>
                </div>
            )}
        </div>
    );
};

export default UploadMoreImages;
